import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import AbuseUserPage from '../AbuseUserPage'
import { getUpcomingDays } from '../../../modules/MyAccount/components/Settings/selectors'
import Loader from '../../../common/Loader'
import { useAuth0 } from '@auth0/auth0-react'
import {
  AbuseStatusResponse,
  Status,
  SuspiciousAbuseStatusResponse
} from '../types'
import { FF_DISCOUNT_ABUSE_LOCK_SCREEN_ENABLED } from '../../../constants/feature-flags'
import { useFlag } from '@unleash/proxy-client-react'
import { getAuthHeaders } from '../../../api/auth-headers'

const isSuspicious = (
  data: AbuseStatusResponse | null
): data is SuspiciousAbuseStatusResponse => data?.status === Status.SUSPICIOUS

export function AbuseDiscountCheck({
  children
}: {
  children: React.ReactNode
}) {
  const lockScreenEnabled = useFlag(FF_DISCOUNT_ABUSE_LOCK_SCREEN_ENABLED)

  if (!lockScreenEnabled) return children

  return <AbuseWrapper>{children}</AbuseWrapper>
}

export function AbuseWrapper({ children }: { children: React.ReactNode }) {
  const dispatch = useDispatch()
  const upcomingDeliveries = useSelector(state => getUpcomingDays(state))
  const { isAuthenticated } = useAuth0()

  // Abuse state
  const isPolling = useRef(false)
  const [data, setData] = useState<AbuseStatusResponse | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [shouldPoll, setShouldPoll] = useState<boolean>(true)

  const loadingUpcomingDays = !upcomingDeliveries

  const isFunnelUser = upcomingDeliveries?.some(day => day.isFirstOrder)

  const isAbuse = isSuspicious(data)

  const checkAbuse = async () => {
    try {
      setLoading(true)
      const headers = await getAuthHeaders()
      const res = await fetch(
        `${process.env.REACT_APP_SUBSCRIPTION_API_URL}/discount-abuse/status`,
        {
          headers,
          credentials: 'include'
        }
      )
      const abuseData = await res.json()
      setData(abuseData)

      // Stop polling if the abuse status is suspicious
      if (abuseData.status === 'suspicious') {
        setShouldPoll(false)
      }
    } catch (err) {
      console.error('abuse > Abuse check failed:', err)
    } finally {
      setLoading(false)
      isPolling.current = true
    }
  }

  useEffect(() => {
    // Won't check abuse
    if (!loadingUpcomingDays && !isFunnelUser) {
      setLoading(false)
      return
    }

    // Wait until upcoming days is loaded so we can check if the user is a funnel user
    // Don't poll if the user is not a funnel user or if the polling has already started
    // Don't poll if 5 minutes have passed (after funnel)
    if (loadingUpcomingDays || !isFunnelUser || !shouldPoll) return

    checkAbuse()

    // Stop polling after 3 minutes (after funnel)
    const timeout = setTimeout(() => {
      setShouldPoll(false)
      clearInterval(interval)
    }, 180000)

    // Polling every 10 seconds
    const interval = setInterval(checkAbuse, 10000)

    return () => {
      clearTimeout(timeout)
      clearInterval(interval)
    }
  }, [isFunnelUser, dispatch, shouldPoll, loadingUpcomingDays])

  // Loading abuse status
  if (loading && !isPolling.current && isAuthenticated) {
    return <Loader />
  }

  // Abuse detected
  if (isAbuse && data) {
    return <AbuseUserPage data={data} />
  }

  // Abuse not detected or not a funnel user
  return <>{children}</>
}
